import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import {useHistory} from 'react-router-dom';
import * as actions from '../_redux/transfersActions';
import * as commonColumnFormatters from '../../_common/column-formatters';
import * as columnFormatters from './column-formatters';
import {useTransfersContext} from '../context/TransfersContext';
import useInfiniteScrollControl from '../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from "../../_common/components/CommonGrid/CommonGrid";
import * as _ from 'lodash';
import moment from 'moment';
import {getPaymentTypeName} from '../../TransfersByAgent/utils';
import {formattingPrice} from '../../_common/utils/format';

export function TransfersTable() {
  const prefix = 'transfersByAgent-transfers-column';
  const history = useHistory();
  const intl = useIntl();
  const [tableRows, setTableRows] = useState([]);
  const columns = useMemo(() => [
    {
      accessor: 'agentData',
      Header: intl.formatMessage({id: `${prefix}-agentData`, defaultMessage: 'Agent'})
    },
    {
      accessor: 'transferPaycheck',
      Header: intl.formatMessage({id: `${prefix}-paycheck`, defaultMessage: 'Paycheck'})
    },
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({id: `${prefix}-receiptID`, defaultMessage: 'Receipt ID'})
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({id: `${prefix}-customer`, defaultMessage: 'Customer'})
    },
    {
      accessor: 'receiptDate',
      Header: intl.formatMessage({id: `${prefix}-receiptDate`, defaultMessage: 'Receipt date'})
    },
    {
      accessor: 'paymentType',
      Header: intl.formatMessage({id: `${prefix}-paymentType`, defaultMessage: 'Payment type'})
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({id: `${prefix}-amount`, defaultMessage: 'Amount'})
    },
    {
      accessor: 'actions',
      Header: intl.formatMessage({id: `${prefix}-actions`, defaultMessage: 'Actions'})
    },
  ], []);
  const data = useMemo(() => [
    ...tableRows
      .map(item => {
        if (item.paycheck.splits > 1) {
          return new Array(item.paycheck.splits)
            .fill(item)
            .map((item, index) => {
              const month = Math.min(item.paycheck.date.month + index, 12);
              const year = (item.paycheck.date.month + index) > 12 ? item.paycheck.date.year + 1 : item.paycheck.date.year;

              return {
                ...item,
                paycheck: {
                  ...item.paycheck,
                  date: {month, year},
                },
                splitNum: index + 1,
              };
            });
        } else {
          return [item];
        }
      })
      .flat()
      .map((item) => {
      const agentID = _.get(item, 'agentId', '');
      const agentFirstName = _.get(item, 'agent.firstName', '');
      const agentLastName = _.get(item, 'agent.lastName', '');
      const agentFullName = [agentFirstName, agentLastName].filter(Boolean).join(' ');
      const {lines, customLines} = item;
      const linesArray = lines ? lines : [];
      const customLinesArray = customLines ? customLines : [];

      const customersFromLines = linesArray.map(line => {
        return [
          _.get(line, 'receipt.customer._id', ''),
          '-',
          _.get(line, 'receipt.customer.name.first', ''),
          _.get(line, 'receipt.customer.name.last', '')
        ].filter(Boolean).join(' ');
      });
      const customersFromCustomLines = customLinesArray.map(item => item.customer ? item.customer : '');
      const customers = [...customersFromLines, ...customersFromCustomLines];

      const datesFromLines = linesArray.map(line => {
        const {receipt} = line;
        return receipt?.date ? moment(receipt.date).format('DD-MM-YYYY') : '';
      });
      const datesFromCustomLines = customLinesArray.map(item => item.creationDate ? item.creationDate : '');
      const receiptDates = [...datesFromLines, ...datesFromCustomLines];

      const paymentTypesFromLines = linesArray.map(line => getPaymentTypeName(line.paymentType));
      const paymentTypesFromCustomLines = customLinesArray.map(item => item.paymentType ? item.paymentType : '');
      const paymentTypes = [...paymentTypesFromLines, ...paymentTypesFromCustomLines];

      const amountsFromLines = linesArray.map(line => {
        return [
          formattingPrice(line.amount ? parseFloat(line.amount).toFixed(2) : 0, true),
          formattingPrice(line.originalAmount ? parseFloat(line.originalAmount).toFixed(2) : 0, true),
        ].filter(Boolean).join('/');
      });
      const amountsFromCustomLines = customLinesArray.map(customLine => {
        const price = customLine.amount ? customLine.amount.toFixed(2) : 0;
        return formattingPrice(price, true);
      });
      const amounts = [...amountsFromLines, ...amountsFromCustomLines];
      const transfersMonthYear = [
        'משכורת ',
        _.get(item, 'paycheck.date.month', ''),
        '.',
        _.get(item, 'paycheck.date.year', '')
      ].filter(Boolean).join('');

      return {
        agentData: columnFormatters.AgentDataColumnFormatter(agentID, agentFullName),
        transferPaycheck: columnFormatters.PaycheckColumnFormatter(item.datetime, transfersMonthYear, item.splitNum),
        receiptID: commonColumnFormatters.SeveralItemsColumnFormatter(item.lineIds),
        customer: commonColumnFormatters.SeveralItemsColumnFormatter(customers, false, false),
        receiptDate: commonColumnFormatters.SeveralItemsColumnFormatter(receiptDates, false, false),
        paymentType: commonColumnFormatters.SeveralItemsColumnFormatter(paymentTypes, false, false),
        amount: commonColumnFormatters.SeveralItemsColumnFormatter(amounts, false, false),
        actions: columnFormatters.ActionsColumnFormatter(item._id, item.agentId, {history}),
      };
    })
  ], [tableRows]);

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'transfers',
    UIContext: useTransfersContext(),
    getNewEntities: actions.getTransfers,
    getNextEntities: actions.getNextTransfers,
    onEntitiesChange: entities => setTableRows(entities),
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (
      <div className={'spinner-container'}>
        <span className={'spinner spinner-md spinner-primary'}/>
      </div>
    ),
    style: {
      overflow: 'inherit'
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      tableProps={useTable({columns, data})}
      customTableStyles={'all-transfers-table'}
    />
  );
}

